/** US reads month-first, everyone else (EU) day-first; follows the app locale. */
export const isUsDateRegion = (locale: string) => /^en([-_]US)?$/i.test(locale);

const capitalize = (text: string) =>
  `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

const toDate = (value: Date | number | string) =>
  value instanceof Date ? value : new Date(value);

/**
 * `Intl.DateTimeFormat` options, with the day and month placed by the viewer's
 * region instead of the browser's locale data. Whenever BOTH a day and a month
 * are asked for the result is composed here:
 *
 *   written  US "Mon, Nov 2, 2026"   EU "Mon, 2 Nov 2026"
 *   numeric  US "11/02/2026"         EU "02.11.2026."
 *
 * Anything else (a month and year, a weekday, a time alone) has no order to
 * disagree about and is left to `Intl`. A time of day, when asked for, follows
 * the date after a comma.
 */
export function formatRegionDate(
  value: Date | number | string,
  locale: string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';

  const { hour, minute, second, timeStyle, dateStyle, ...rest } = options;
  const hasTime =
    hour !== undefined || minute !== undefined || second !== undefined;
  const { day, month, weekday, year, timeZone } = rest;
  const composes = !dateStyle && !timeStyle && day && month;

  if (!composes) {
    return new Intl.DateTimeFormat(locale, options).format(date);
  }

  const numeric = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'numeric',
    timeZone,
    year: 'numeric',
  }).formatToParts(date);
  const part = (type: string) =>
    Number(numeric.find((p) => p.type === type)?.value);
  const dd = part('day');
  const mm = part('month');
  const yyyy = part('year');

  const us = isUsDateRegion(locale);
  const pad = (n: number) => String(n).padStart(2, '0');
  const dayText = day === '2-digit' ? pad(dd) : String(dd);

  let datePart: string;
  if (month === 'numeric' || month === '2-digit') {
    const monthText = pad(mm);
    datePart = us
      ? [monthText, pad(dd), ...(year ? [yyyy] : [])].join('/')
      : `${pad(dd)}.${monthText}.${year ? `${yyyy}.` : ''}`;
  } else {
    const monthName = capitalize(
      new Intl.DateTimeFormat(locale, { month, timeZone })
        .format(date)
        .replace(/\./g, ''),
    );
    datePart = us
      ? `${monthName} ${dayText}${year ? `, ${yyyy}` : ''}`
      : `${dayText} ${monthName}${year ? ` ${yyyy}` : ''}`;
  }

  if (weekday) {
    datePart = `${new Intl.DateTimeFormat(locale, { timeZone, weekday }).format(date)}, ${datePart}`;
  }

  if (!hasTime) return datePart;
  const time = new Intl.DateTimeFormat(locale, {
    hour,
    hourCycle: rest.hourCycle,
    minute,
    second,
    timeZone,
  }).format(date);
  return `${datePart}, ${time}`;
}
